import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { api, apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Card, Btn, Modal } from "../../components/UI";
import { FLabel, FInput, FSelect, FTextarea, CheckBox, SelectWithCustom } from "../engagement/FormControls";
import { DateField } from "../engagement/DateField";
import { SignModal } from "../../components/SignatureCanvas";
import { ClientSelect, useClients, SignatureDisplay } from "./shared";
import { PROGRAMS as PROGRAMS_FALLBACK, SERVICES } from "../engagement/constants"; // eslint-disable-line no-unused-vars
import { usePrograms } from "../../lib/usePrograms";
import { ArrowLeft, Lock, PenLine, Link2 } from "lucide-react";
import ClinicalRecordLockControls from "../../components/ClinicalRecordLockControls";
import { toast } from "sonner";
import dayjs from "dayjs";
import { LateEntryWarning } from "../../lib/lateEntry";
import { useSaveShortcut, SAVE_HINT } from "../../lib/useSaveShortcut";
import { MedicationReview, EMPTY_REVIEW } from "./MedicationReview";

const FORMAT_FIELDS = {
  SOAP: [["subjective", "Subjective - client's report, quotes, stated concerns"],
    ["objective", "Objective - observations, MSE, measurable data"],
    ["assessment", "Assessment - clinical interpretation, progress toward goals"],
    ["plan", "Plan - next steps, interventions, homework, follow-up"]],
  DAP: [["data", "Data - what happened in session (subjective + objective)"],
    ["assessment", "Assessment - clinical interpretation, response to intervention"],
    ["plan", "Plan - next steps, follow-up, referrals"]],
};
const RISK_FLAGS = ["Suicidal ideation", "Homicidal ideation", "Self-harm", "Substance use concern", "Abuse/neglect concern"];
const EMPTY = { client_id: "", date: dayjs().format("YYYY-MM-DD"), time: "09:00", duration_minutes: "50", service: "", program: "", format: "SOAP", fields: {}, risk_flags: [], status: "draft", med_review: EMPTY_REVIEW };

export default function ProgressNoteEditor() {
  const PROGRAMS = usePrograms();
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.from || { url: "/clinical/progress-notes", label: "Progress Notes" };
  const goBack = () => navigate(returnTo.url);
  const { user } = useAuth();
  const clients = useClients();
  const [form, setForm] = useState(EMPTY);
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const preselect = searchParams.get("client");
    if (preselect && isNew) setForm((f) => ({ ...f, client_id: preselect }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [note, setNote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  const [cosignOpen, setCosignOpen] = useState(false);
  const [addendumText, setAddendumText] = useState("");
  const [addendumSignOpen, setAddendumSignOpen] = useState(false);
  const [goals, setGoals] = useState([]);
  const [medWarn, setMedWarn] = useState(false);

  const load = () => {
    if (isNew) return;
    api.get(`/progress-notes/${id}`).then((r) => { setNote(r.data); setForm(r.data); })
      .catch(() => { toast.error("Note not found"); navigate("/clinical/progress-notes"); });
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const locked = note?.status === "signed" || note?.status === "pending_cosign" || note?.locked;
  const pendingCosign = note?.status === "pending_cosign";
  const canCosign = pendingCosign && (user?.role === "admin" || user?.id === note?.cosign_supervisor_id);
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  useEffect(() => {
    if (form.client_id) api.get(`/clients/${form.client_id}/goals`).then((r) => setGoals(r.data)).catch(() => setGoals([]));
    else setGoals([]);
  }, [form.client_id]);
  const setSection = (k, v) => setForm((f) => ({ ...f, fields: { ...f.fields, [k]: v } }));
  const toggleRisk = (r) => setForm((f) => ({ ...f, risk_flags: f.risk_flags.includes(r) ? f.risk_flags.filter((x) => x !== r) : [...f.risk_flags, r] }));

  const save = async (status) => {
    if (!form.client_id) { toast.error("Select a client"); return; }
    setBusy(true);
    try {
      const body = { ...form, status };
      let r;
      if (isNew) r = await api.post("/progress-notes", body);
      else r = await api.put(`/progress-notes/${id}`, body);
      toast.success(status === "completed" ? "Note completed" : "Draft saved");
      if (isNew) navigate(`/clinical/progress-notes/${r.data.id}`, { replace: true });
      else { setNote(r.data); setForm(r.data); }
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  useSaveShortcut(() => { if (!busy) save("draft"); }, !locked);
  const startSign = () => (form.med_review?.confirmed ? setSignOpen(true) : setMedWarn(true));
  const reviewMeds = () => { setMedWarn(false); document.getElementById("pn-med-review")?.scrollIntoView({ behavior: "smooth", block: "center" }); };

  const sign = async ({ name, image }) => {
    setBusy(true);
    try {
      await api.put(`/progress-notes/${id}`, form);
      const r = await api.put(`/progress-notes/${id}/sign`, { name, image });
      setNote(r.data); setForm(r.data); setSignOpen(false);
      toast.success(r.data.status === "pending_cosign" ? "Note signed - awaiting supervisor co-signature" : "Note signed & locked");
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  const cosign = async ({ name, image }) => {
    setBusy(true);
    try {
      const r = await api.put(`/progress-notes/${id}/cosign`, { name, image });
      setNote(r.data); setForm(r.data); setCosignOpen(false);
      toast.success("Note co-signed & locked");
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  const addAddendum = async ({ name, image }) => {
    setBusy(true);
    try {
      const r = await api.post(`/progress-notes/${id}/addendum`, { text: addendumText, name, image });
      setNote(r.data); setForm(r.data); setAddendumSignOpen(false); setAddendumText("");
      toast.success("Addendum added");
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  return (
    <div data-testid="progress-note-editor">
      <button data-testid="pn-back-btn" onClick={goBack} className="mb-3 flex items-center gap-1 text-sm text-[#7B6157] hover:underline">
        <ArrowLeft size={15} /> Back to {returnTo.label}
      </button>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-xl font-bold text-[#3E2723]">{isNew ? "New Progress Note" : `${form.format} Note - ${note?.client_name || ""}`}</h1>
        <div className="flex flex-wrap items-center gap-2">
          {note?.status === "signed" && !note?.locked && <span data-testid="pn-locked-banner" className="inline-flex items-center gap-1.5 rounded-full bg-[#E8F5E9] px-3.5 py-1.5 text-xs font-bold text-[#2E7D32]"><Lock size={12} /> Signed</span>}
          {pendingCosign && <span data-testid="pn-pending-cosign-banner" className="inline-flex items-center gap-1.5 rounded-full bg-[#FFF3E0] px-3.5 py-1.5 text-xs font-bold text-[#B84A00]"><Lock size={12} /> Awaiting Co-Signature</span>}
          {!isNew && note && (
            <ClinicalRecordLockControls kind="progress_note" record={note} onChange={load}
              amendableFields={[
                { key: "service", label: "Service" },
                { key: "program", label: "Program" },
                { key: "duration_minutes", label: "Duration (min)" },
              ]} />
          )}
        </div>
      </div>

      <Card className="p-6 space-y-6">
        <LateEntryWarning date={form.date} />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-x-5 gap-y-4">
          <div className="xl:col-span-2"><FLabel>Client</FLabel><ClientSelect clients={clients} value={form.client_id} onChange={(v) => setForm((f) => ({ ...f, client_id: v, med_review: EMPTY_REVIEW }))} testId="pn-client-select" disabled={!isNew || locked} /></div>
          <div><FLabel>Date of Service</FLabel><DateField testId="pn-date" value={form.date} onChange={(v) => setField("date", v)} noFuture /></div>
          <div><FLabel>Time</FLabel><FInput type="time" data-testid="pn-time" value={form.time} onChange={(e) => setField("time", e.target.value)} disabled={locked} /></div>
          <div><FLabel>Duration (min)</FLabel><FInput data-testid="pn-duration" value={form.duration_minutes} onChange={(e) => setField("duration_minutes", e.target.value)} disabled={locked} /></div>
          <div><FLabel>Provider</FLabel><FInput value={note?.provider_name || `${user?.first_name || ""} ${user?.last_name || ""}`} disabled /></div>
          <div className="xl:col-span-2"><FLabel>Service</FLabel><SelectWithCustom testId="pn-service" value={form.service} onChange={(v) => setField("service", v)} options={SERVICES} customPlaceholder="Enter service" /></div>
          <div className="xl:col-span-2"><FLabel>Program</FLabel><SelectWithCustom testId="pn-program" value={form.program} onChange={(v) => setField("program", v)} options={PROGRAMS} customPlaceholder="Enter program" /></div>
          <div className="xl:col-span-2"><FLabel>Note Format</FLabel>
            <div className="inline-flex rounded-full border border-[#D7CCC8] bg-white p-1">
              {["SOAP", "DAP"].map((fmt) => (
                <button key={fmt} type="button" data-testid={`pn-format-${fmt.toLowerCase()}`} disabled={locked}
                  onClick={() => setField("format", fmt)}
                  className={`rounded-full px-6 py-2 text-sm font-bold transition-colors ${form.format === fmt ? "bg-[#4E342E] text-white" : "text-[#7B6157] hover:bg-[#F5F2EB]"}`}>{fmt}</button>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {FORMAT_FIELDS[form.format].map(([key, label]) => (
            <div key={key}>
              <FLabel>{label}</FLabel>
              <FTextarea data-testid={`pn-field-${key}`} rows={3} value={form.fields?.[key] || ""} onChange={(e) => setSection(key, e.target.value)} disabled={locked} />
            </div>
          ))}
        </div>

        <div>
          <FLabel>Risk flags (check all noted this session)</FLabel>
          <div className="flex flex-wrap gap-2 mt-1">
            {RISK_FLAGS.map((r) => (
              <CheckBox key={r} label={r} checked={form.risk_flags?.includes(r)} onChange={() => !locked && toggleRisk(r)}
                testId={`pn-risk-${r.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />
            ))}
          </div>
        </div>

        <div data-testid="pn-golden-thread">
          <FLabel>Golden Thread - link this note to treatment goals</FLabel>
          {!form.client_id ? <p className="text-xs text-[#84675B] mt-1">Select a client to see their treatment goals.</p> :
            goals.length === 0 ? <p className="text-xs text-[#84675B] mt-1">No treatment goals on file for this client yet.</p> : (
            <div className="mt-1 space-y-1.5">
              {goals.map((g) => {
                const on = (form.linked_goals || []).some((lg) => lg.key === g.key);
                return (
                  <button key={g.key} type="button" data-testid={`pn-goal-link-${g.key}`} disabled={locked} aria-pressed={on}
                    onClick={() => setField("linked_goals", on ? form.linked_goals.filter((lg) => lg.key !== g.key)
                      : [...(form.linked_goals || []), { key: g.key, plan_id: g.plan_id, goal: g.goal, problem: g.problem }])}
                    className={`flex w-full items-start gap-2 rounded-xl border px-3.5 py-2.5 text-left text-sm transition-colors ${on ? "border-[#4E342E] bg-[#4E342E] text-white" : "border-[#EFEBE0] bg-[#FDFCF8] text-[#5D4037] hover:bg-[#F5F2EB]"} ${locked ? "opacity-70 cursor-default" : ""}`}>
                    <Link2 size={14} className={`mt-0.5 shrink-0 ${on ? "text-[#FFB300]" : "text-[#84675B]"}`} />
                    <span className="min-w-0">
                      <span className="block font-semibold truncate">{g.goal}</span>
                      <span className={`block text-xs truncate ${on ? "text-white/70" : "text-[#84675B]"}`}>{g.problem || g.plan_title}{g.target_date ? ` · target ${g.target_date}` : ""}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <MedicationReview clientId={form.client_id} review={form.med_review} locked={locked} onChange={(v) => setField("med_review", v)} />

        {note?.signature && <SignatureDisplay sig={note.signature} label="Provider Signature" />}
        {note?.cosignature && <SignatureDisplay sig={note.cosignature} label="Supervisor Co-Signature" />}

        {canCosign && (
          <div className="flex justify-end border-t border-[#F1EEE7] pt-4">
            <Btn data-testid="pn-cosign-btn" onClick={() => setCosignOpen(true)} disabled={busy}><PenLine size={14} /> Co-Sign & Lock</Btn>
          </div>
        )}

        {locked && (
          <div className="border-t border-[#F1EEE7] pt-4" data-testid="pn-addendum-section">
            <h3 className="font-heading text-sm font-bold text-[#3E2723] mb-2">Addendums</h3>
            {(note?.addendums || []).map((a) => (
              <div key={a.id} data-testid={`pn-addendum-${a.id}`} className="mb-2 rounded-xl bg-[#F9F5EE] border border-[#F1E8DA] p-4">
                <p className="text-sm text-[#3E2723] whitespace-pre-wrap">{a.text}</p>
                <div className="mt-2 flex items-center justify-between text-xs text-[#7B6157]">
                  <span>{a.author_name}</span><span>{dayjs(a.created_at).format("MM/DD/YYYY h:mm A")}</span>
                </div>
              </div>
            ))}
            {(note?.addendums || []).length === 0 && <p className="text-xs text-[#84675B] mb-2">No addendums. The original note is immutable - corrections and additions are recorded here.</p>}
            <FTextarea data-testid="pn-addendum-input" rows={2} placeholder="Write an addendum to this signed note…"
              value={addendumText} onChange={(e) => setAddendumText(e.target.value)} />
            <div className="mt-2 flex justify-end">
              <Btn data-testid="pn-addendum-add-btn" variant="outline" disabled={!addendumText.trim() || busy}
                onClick={() => setAddendumSignOpen(true)}><PenLine size={13} /> Sign & Attach Addendum</Btn>
            </div>
          </div>
        )}

        {!locked && (
          <div className="flex flex-wrap justify-end gap-3 pt-2 border-t border-[#F1EEE7]">
            <Btn data-testid="pn-save-draft-btn" variant="outline" onClick={() => save("draft")} disabled={busy} title={`Save draft (${SAVE_HINT})`} aria-keyshortcuts="Control+S">Save Draft</Btn>
            <Btn data-testid="pn-complete-btn" variant="outline" onClick={() => save("completed")} disabled={busy}>Mark Completed</Btn>
            {!isNew && <Btn data-testid="pn-sign-btn" onClick={startSign} disabled={busy}><PenLine size={14} /> Sign & Lock</Btn>}
          </div>
        )}
      </Card>

      <Modal open={medWarn} onClose={() => setMedWarn(false)} title="Medication review not confirmed">
        <div data-testid="pn-med-warn">
          <p className="text-sm text-[#5D4037]">You have not confirmed the medication review for this session. You can still sign, and the note will show that the review was not confirmed.</p>
          <div className="mt-5 flex flex-wrap justify-end gap-2">
            <Btn variant="outline" data-testid="pn-med-warn-review" onClick={reviewMeds}>Review medications</Btn>
            <Btn data-testid="pn-med-warn-sign" onClick={() => { setMedWarn(false); setSignOpen(true); }}><PenLine size={14} /> Sign anyway</Btn>
          </div>
        </div>
      </Modal>
      <SignModal open={signOpen} onClose={() => setSignOpen(false)} onSign={sign} busy={busy}
        title="Sign Progress Note" defaultName={`${user?.first_name || ""} ${user?.last_name || ""}`.trim()} />
      <SignModal open={cosignOpen} onClose={() => setCosignOpen(false)} onSign={cosign} busy={busy}
        title="Co-Sign Progress Note (Supervisor)" defaultName={`${user?.first_name || ""} ${user?.last_name || ""}`.trim()} />
      <SignModal open={addendumSignOpen} onClose={() => setAddendumSignOpen(false)} onSign={addAddendum} busy={busy}
        title="Sign Addendum" defaultName={`${user?.first_name || ""} ${user?.last_name || ""}`.trim()} />
    </div>
  );
}
